import { CruiseSearchContext } from 'contexts/CruiseSearchContext';
import { CRUISING_PORT_CODES } from 'contexts/CruiseSearchContext/constants';
import { addDays, isEqual, parseISO } from 'date-fns';
import localizedFormat from 'lib/format';
import { getDockedTimeRange } from 'lib/utilities/CruiseCard';
import { useContext, useMemo } from 'react';
import { Cruise } from 'types';
import { DaySrcType } from 'types/DaySrc';
import {
	MapCruiseCardGridActivity,
	MapCruiseCardGridDate,
	MapCruiseCardGridDay,
	MapCruiseCardGridItem,
	MapCruiseCardGrid as MapCruiseCardGridList,
	MapCruiseCardGridPort,
} from './MapCruiseCardGrid.styled';

function MapCruiseCardGrid({ cruise }: { cruise: Cruise }) {
	const { globalization }: any = useContext(CruiseSearchContext);
	const { compareDictionary, itineraryCardsDictionary } = globalization?.dictionaries || {};
	const { labelCompareDescriptorDay: dayLabel } = compareDictionary;
	const { labelDeparts, labelArrival, labelDocked, labelAtSea } = itineraryCardsDictionary;
	const {
		featuredSailing,
		cruiseSrc: {
			id,
			masterSailing: {
				itinerary: { days = [] },
			},
		},
	} = cruise;
	const earliestSailingDateObject = parseISO(featuredSailing?.startDate);
	const latestSailingDateObject = parseISO(featuredSailing?.endDate);

	const formatDate = (index: number) => {
		const currentDateOfActivityObject = addDays(earliestSailingDateObject, index);
		return localizedFormat(currentDateOfActivityObject, 'EEE, MMM dd, yyyy');
	};

	const portActivities = useMemo(() => {
		let intDateLineDay = 0;
		let filteredDays = days;
		const lastPortDate = addDays(earliestSailingDateObject, days[days.length - 1]?.number - 1);

		if (!isEqual(lastPortDate, latestSailingDateObject)) {
			intDateLineDay = days?.find((day) => day.type === DaySrcType.CruisingIntDate)?.number;
			filteredDays = days?.filter((day) => day.type !== DaySrcType.CruisingIntDate);
		}

		return filteredDays?.flatMap(({ number, ports }) => {
			return ports.map((port) => {
				const atSea = CRUISING_PORT_CODES.includes(port.port.code);
				const label = atSea ? labelAtSea : `${port.port.name}, ${port.port.region}`;
				const activitySchedule = getDockedTimeRange({
					arrivalTime: port.arrivalTime,
					departureTime: port.departureTime,
					labels: { departs: labelDeparts, arrival: labelArrival, docked: labelDocked },
				});
				return { dayNumber: intDateLineDay > 0 && number > intDateLineDay ? number - 1 : number, label, activitySchedule };
			});
		});
	}, [days, labelArrival, labelAtSea, labelDeparts, labelDocked]);

	return (
		<MapCruiseCardGridList>
			{portActivities.map((port, index) => (
				<MapCruiseCardGridItem key={`${id}_${index}`}>
					<MapCruiseCardGridDay>{`${dayLabel} ${port.dayNumber}`}</MapCruiseCardGridDay>
					<MapCruiseCardGridDate>{formatDate(port.dayNumber - 1)}</MapCruiseCardGridDate>
					<MapCruiseCardGridPort>{port.label}</MapCruiseCardGridPort>
					<MapCruiseCardGridActivity>{`${port.activitySchedule}`}</MapCruiseCardGridActivity>
				</MapCruiseCardGridItem>
			))}
		</MapCruiseCardGridList>
	);
}

export default MapCruiseCardGrid;
